import { useMemo } from 'react';
import { keepPreviousData, queryOptions, skipToken, useQueries, useQuery } from '@tanstack/react-query';
import type { operations } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { accountUserKeys, parseAccountUser, type AccountUser } from './entity';

const STALE_TIME = 60_000;

/** Server page size of the users index — the handler caps every response at 20 rows. */
export const ACCOUNT_USERS_PAGE_SIZE = 20;

/** @public Fetch a single account user by id. */
export async function getAccountUser(accountId: number, userId: string): Promise<AccountUser> {
  const data = await hydraUiApi.users.get({
    params: { path: { account_id: String(accountId), id: userId } },
  });
  if (data.user == null) {
    throw new Error(`User ${userId} not found on account ${accountId}`);
  }
  return parseAccountUser(data.user);
}

/**
 * @public Fetch the first page of the account's users (the users index).
 *
 * The index endpoint is always paginated — it returns at most one page
 * ({@link ACCOUNT_USERS_PAGE_SIZE} rows), never the whole account — so this is
 * the first page only. Use {@link getAccountUsersPage} to page through the rest.
 */
export async function getAccountUsers(accountId: number): Promise<AccountUser[]> {
  const data = await hydraUiApi.users.list({ params: { path: { account_id: String(accountId) } } });
  return (data.users ?? []).map(parseAccountUser);
}

/** @public */
export function getAccountUsersQueryOptions(accountId: number) {
  return queryOptions({
    queryKey: accountUserKeys.list(accountId, undefined),
    queryFn: () => getAccountUsers(accountId),
    staleTime: STALE_TIME,
  });
}

/** @public Non-suspending read of the account's first page of users. */
export function useAccountUsers(accountId: number) {
  return useQuery(getAccountUsersQueryOptions(accountId));
}

/** One server page of account users, plus the pagination totals from `meta`. */
export type AccountUsersPage = {
  users: AccountUser[];
  /** Total accessible users after the search filter — drives the paginator. */
  total: number;
  /** 1-indexed page the server echoed back. */
  page: number;
  /** Server page size (`meta.pagination.size`). */
  size: number;
};

/**
 * The users-index query params, aliased straight from the generated OpenAPI operation so they can't
 * drift from the wire contract. All optional, matching the backend (`page` defaults to 1 server-side;
 * omit `workspace` for all workspaces). `sort` accepts `'lastLoginAt'` (default) or
 * `'normalizedAccountPermissions.count'`. Matches the keys Ember's `store.query('user', …)` used.
 */
export type AccountUsersPageParams = NonNullable<operations['get-v1-accounts-account_id-users']['parameters']['query']>;

/**
 * @public One server page of account users, with the same server-side `page`,
 * `search`, `sort`, `asc`, and `workspace` filters the Ember `settings.team`
 * route used (`store.query('user', …)`). `meta.pagination.total` drives the
 * paginator.
 */
export async function getAccountUsersPage(
  accountId: number,
  { page, search, sort, asc, workspace }: AccountUsersPageParams,
): Promise<AccountUsersPage> {
  const trimmed = search?.trim();
  const data = await hydraUiApi.users.list({
    params: {
      path: { account_id: String(accountId) },
      query: {
        page,
        search: trimmed == null || trimmed === '' ? undefined : trimmed,
        sort,
        asc,
        workspace,
      },
    },
  });
  const { page: echoedPage, size, total } = data.meta.pagination;
  return { users: (data.users ?? []).map(parseAccountUser), total, page: echoedPage, size };
}

/** @public */
export function getAccountUsersPageQueryOptions(accountId: number, params: AccountUsersPageParams) {
  return queryOptions({
    queryKey: [...accountUserKeys.lists(accountId), 'page', params] as const,
    queryFn: () => getAccountUsersPage(accountId, params),
    // The Ember original used `store.query('user', …)` (not `findAll`) precisely because it always
    // wants the latest server state — so this never serves a cached page. staleTime: 0 revalidates
    // on every search / filter / sort / page change and on mount (e.g. landing here after the
    // Ember add / invite handoff, which mutates users outside Hydra's cache). keepPreviousData
    // keeps the prior rows visible during the refetch.
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** @public Server-paginated, server-searched account users for the workspace users table. */
export function useAccountUsersPage(accountId: number, params: AccountUsersPageParams) {
  return useQuery(getAccountUsersPageQueryOptions(accountId, params));
}

/**
 * @public Resolve account users by id, one `GET /users/{id}` each — the React
 * port of Ember's async `hasMany('user')`. Used to show a workspace's assigned
 * members (their email + sign-in state) independently of the paginated list,
 * which only ever holds one page. Pass `enabled: false` to defer the fetches
 * until the members are actually needed (e.g. only when the test-address
 * dropdown is shown).
 */
export function useAccountUsersByIds(
  accountId: number,
  userIds: readonly string[],
  { enabled = true }: { enabled?: boolean } = {},
): { users: AccountUser[]; isLoading: boolean; isError: boolean } {
  const active = enabled && accountId > 0;
  const results = useQueries({
    queries: userIds.map((id) => ({
      queryKey: accountUserKeys.detail(accountId, id),
      queryFn: active ? () => getAccountUser(accountId, id) : skipToken,
      staleTime: STALE_TIME,
    })),
  });
  const isLoading = results.some((result) => result.isLoading);
  // A failed member fetch drops that user from `users` even though its membership was never
  // disproven — callers use this to avoid concluding a still-assigned member was removed.
  const isError = results.some((result) => result.isError);
  const users = useMemo(
    () => results.map((result) => result.data).filter((user): user is AccountUser => user != null),
    // results identity changes per fetch; keyed by the requested ids (matches the
    // service-account token-stats pattern).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above
    [userIds, results],
  );
  return { users, isLoading, isError };
}

/**
 * Non-suspending read of a single account user by id. Pass `undefined` /
 * empty for `userId` to skip the fetch (`data` stays `undefined`) — useful
 * when the id is only sometimes present (e.g. who granted account access).
 *
 * @public
 */
export function useAccountUser(accountId: number, userId: string | undefined) {
  const hasUserId = userId != null && userId !== '';
  return useQuery({
    queryKey: hasUserId ? accountUserKeys.detail(accountId, userId) : (['account', accountId, 'user', 'none'] as const),
    queryFn: hasUserId ? () => getAccountUser(accountId, userId) : skipToken,
    staleTime: STALE_TIME,
  });
}
